/**
 * Dynamic Classical Yogas Integration
 * 
 * This module provides a function to dynamically load classical yogas from
 * the database and merge them with the existing RULES array.
 * 
 * Usage:
 * ```typescript
 * import { loadAndMergeClassicalYogas } from './classicalYogasIntegration';
 * const allRules = await loadAndMergeClassicalYogas(RULES);
 * ```
 */

import { fetchClassicalYogas, convertYogasToRules } from './classicalYogasLoader';
import type { Rule } from './rules';

/**
 * Cache for loaded yogas to avoid repeated database queries
 */
let cachedYogaRules: Rule[] | null = null;
let lastFetchTime: number = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Load classical yogas from database and convert to Rule format
 */
export async function loadClassicalYogasAsRules(): Promise<Rule[]> {
  const now = Date.now();
  
  // Return cached rules if still valid
  if (cachedYogaRules && (now - lastFetchTime < CACHE_TTL)) {
    return cachedYogaRules;
  }
  
  try {
    const yogas = await fetchClassicalYogas();
    
    // Convert to Rule format
    const rules = convertYogasToRules(yogas);
    
    // Cache the results
    cachedYogaRules = rules;
    lastFetchTime = now;
    
    return rules;
  } catch (error) {
    console.error('[loadClassicalYogasAsRules] Error loading yogas:', error);
    return [];
  }
}

/**
 * Merge classical yogas with existing rules
 * 
 * @param existingRules - The current RULES array
 * @param options - Configuration options
 * @returns Combined array of rules
 */
export async function loadAndMergeClassicalYogas(
  existingRules: Rule[],
  options: {
    prepend?: boolean; // If true, add yogas before existing rules (higher priority)
    filterCategories?: string[]; // Only load specific yoga categories
  } = {}
): Promise<Rule[]> {
  const { prepend = false, filterCategories } = options;
  
  // Load yoga rules
  let yogaRules = await loadClassicalYogasAsRules();
  
  // Filter by category if specified
  if (filterCategories && filterCategories.length > 0) {
    yogaRules = yogaRules.filter(rule => {
      // Extract category from rule key (e.g., 'ravi_vesi' -> 'ravi')
      const category = rule.key.split('_')[0];
      return filterCategories.includes(category);
    });
  }
  
  // Merge with existing rules
  const mergedRules = prepend
    ? [...yogaRules, ...existingRules]
    : [...existingRules, ...yogaRules];
  
  return mergedRules;
}

/**
 * Clear the cache (useful for testing or when yogas are updated in database)
 */
export function clearClassicalYogasCache(): void {
  cachedYogaRules = null;
  lastFetchTime = 0;
}

/**
 * Preload classical yogas (call this on app startup)
 */
export async function preloadClassicalYogas(): Promise<void> {
  await loadClassicalYogasAsRules();
}

/**
 * Get the last loaded yoga rules (synchronous, returns empty array if not loaded)
 * Use this for synchronous access after preloading
 */
export function getLastLoadedYogaRules(): Rule[] {
  return cachedYogaRules || [];
}
